// Etsy profit calculator — framework-independent pure computation.
//
// Logic locked per Volkan spec 2026-05-31: USD-canonical, TRY fixed fees
// converted via usdToLocalRate. Order of operations is load-bearing —
// VAT (sellerFeeTax) applies on top of the SUM of all Etsy fees, and the
// offsite-ads fee is capped per order.

export type CountryCode = 'US' | 'TR'

export interface CountryProfile {
  listingFee: number
  transactionRate: number
  paymentRate: number
  paymentFixedUsd: number
  paymentFixedTry: number
  regulatoryRate: number
  depositFeeTry: number
}

const COUNTRY_PROFILES: Record<CountryCode, CountryProfile> = {
  US: {
    listingFee: 0.2,
    transactionRate: 0.065,
    paymentRate: 0.03,
    paymentFixedUsd: 0.25,
    paymentFixedTry: 0,
    regulatoryRate: 0,
    depositFeeTry: 0,
  },
  TR: {
    listingFee: 0.2,
    transactionRate: 0.065,
    paymentRate: 0.065,
    paymentFixedUsd: 0,
    paymentFixedTry: 14,
    regulatoryRate: 0.0227,
    depositFeeTry: 42,
  },
}

export const OFFSITE_ADS_CAP_USD = 100

export type OffsiteAdsRate = 0 | 0.12 | 0.15

export interface EtsyProfitInput {
  itemPrice: number
  unitCost: number
  shippingCost: number
  adCostPerOrder: number
  shippingCharged: number
  giftWrapCharged: number
  packagingCost: number
  offsiteAdsRate: OffsiteAdsRate
  currencyConversionEnabled: boolean
  applyDepositFee: boolean
  sellerFeeTaxPercent: number
  usdToLocalRate: number
  quantity: number
  countryCode: CountryCode
}

export interface EtsyFeeBreakdown {
  listingFee: number
  transaction: number
  payment: number
  regulatory: number
  offsiteAds: number
  currencyConv: number
  depositFee: number
  tax: number
}

export interface MonthlyProjection {
  perDay: number
  amount: number
}

export interface EtsyProfitResult {
  grossRevenue: number
  totalEtsyFees: number
  operatingCost: number
  netProfit: number
  profitMargin: number
  breakdown: EtsyFeeBreakdown
  monthly: MonthlyProjection[]
  /** True when a TRY fixed fee was requested but usdToLocalRate <= 0 (fee skipped). */
  rateWarning: boolean
}

export function calcEtsyProfit(i: EtsyProfitInput): EtsyProfitResult {
  const p = COUNTRY_PROFILES[i.countryCode] || COUNTRY_PROFILES.US
  const validRate = i.usdToLocalRate > 0
  const rate = validRate ? i.usdToLocalRate : 1
  const qty = i.quantity || 1

  const grossRevenue =
    (i.itemPrice || 0) + (i.shippingCharged || 0) + (i.giftWrapCharged || 0)

  const listingFee = p.listingFee * qty
  const transaction = grossRevenue * p.transactionRate
  const paymentFixedTryUsd = validRate ? p.paymentFixedTry / rate : 0
  const payment = grossRevenue * p.paymentRate + p.paymentFixedUsd + paymentFixedTryUsd
  const regulatory = grossRevenue * p.regulatoryRate
  const offsiteAds = Math.min(grossRevenue * (i.offsiteAdsRate || 0), OFFSITE_ADS_CAP_USD)
  const currencyConv = i.currencyConversionEnabled ? grossRevenue * 0.025 : 0
  const depositFee = i.applyDepositFee && validRate ? p.depositFeeTry / rate : 0

  const feeSubtotal =
    listingFee + transaction + payment + regulatory + offsiteAds + currencyConv + depositFee
  const tax = feeSubtotal * ((i.sellerFeeTaxPercent || 0) / 100)
  const totalEtsyFees = feeSubtotal + tax

  const operatingCost =
    (i.unitCost || 0) + (i.shippingCost || 0) + (i.adCostPerOrder || 0) + (i.packagingCost || 0)

  const netProfit = grossRevenue - totalEtsyFees - operatingCost
  const profitMargin = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0

  const needsTryFixed =
    p.paymentFixedTry > 0 || (i.applyDepositFee && p.depositFeeTry > 0)
  const rateWarning = needsTryFixed && !validRate

  return {
    grossRevenue,
    totalEtsyFees,
    operatingCost,
    netProfit,
    profitMargin,
    breakdown: { listingFee, transaction, payment, regulatory, offsiteAds, currencyConv, depositFee, tax },
    monthly: [5, 10, 20, 50].map((s) => ({ perDay: s, amount: netProfit * s * 30 })),
    rateWarning,
  }
}
